<script setup>
import { computed, ref, shallowRef } from 'vue';
import { useDisplay } from "vuetify";
import BTTReport from '~/components/reports/btt-report.vue';
import GroutingNextGenReport from '~/components/reports/groutingnextgen-report.vue';
import GroutingSKIDReport from '~/components/reports/groutingskid-report.vue';
import GroutingTCPReport from '~/components/reports/groutingtcp-report.vue';
import { DatabaseService } from '~/services/DatabaseService';
import { MachineType } from '~/utils/machineType';

const { mobile, height } = useDisplay();
const dashboardStyle = computed(() => ({
  height: `${mobile.value ? height.value - 235 : height.value - 175}px`
}));

const head = ref({ title: 'Report analysis' });
useHead(head);

const { processDatabase: runDataEngine } = useDataEngine()

const status = ref('Waiting for database...')
const showUploadZone = ref(false)
const autoCorrect = ref(true)
const showAutoCorrectDialog = ref(false)
const pendingBytes = shallowRef(null)
const machineType = ref(null)
const isDecoding = ref(false)

const rawUserData = ref(null)
const worksiteData = ref(null)
const machineInfo = ref(null)
const rawDecodedData = shallowRef(null)

const viewMap = {
  [MachineType.GroutingRigSKID]: GroutingSKIDReport,
  [MachineType.GroutingRigNextGen]: GroutingNextGenReport,
  [MachineType.GroutingRigTCP]: GroutingTCPReport,
  [MachineType.BoltTensileTester]: BTTReport
}

const currentView = computed(() => {
  return viewMap[machineType.value] || null
})

async function processDatabase(applyCorrection) {
  if (!pendingBytes.value) return;

  showAutoCorrectDialog.value = false;
  isDecoding.value = true;
  await new Promise(resolve => setTimeout(resolve, 100));

  try {
    rawDecodedData.value = await runDataEngine(pendingBytes.value, applyCorrection);
    machineType.value = machineInfo.value[2];
    status.value = "Database loaded successfully";
  } catch (err) {
    status.value = err.message
    console.error("DataEngine processing failed:", err)
  } finally {
    isDecoding.value = false;
    pendingBytes.value = null;
  }
}

async function onDrop(event) {
  event.preventDefault()
  status.value = "Loading database..."
  showUploadZone.value = false
  const file = event.dataTransfer.files[0]
  const reader = new FileReader()

  reader.onload = async (e) => {
    try {
        const bytes = new Uint8Array(e.target.result)
        const dbService = new DatabaseService()
        await dbService.load(e.target.result)
        rawUserData.value = dbService.getUser()
        worksiteData.value = dbService.getWorksite()
        machineInfo.value = dbService.getMachine()

        pendingBytes.value = bytes;
        const mType = machineInfo.value[2];
        const supportedTypes = [
          MachineType.GroutingRigSKID,
          MachineType.GroutingRigNextGen,
          MachineType.GroutingRigTCP
        ];

        if (supportedTypes.includes(mType)) {
          showAutoCorrectDialog.value = true;
        } else {
          await processDatabase(false);
        }
    } catch (err) {
        status.value = err.message
        console.error("Failed to read database file:", err.message)
        isDecoding.value = false;
    }
  }
  reader.readAsArrayBuffer(file)
}

function onDragOver(event) {
  event.preventDefault()
}
</script>

<template>
  <v-container fluid>
    <!-- Slim Toolbar -->
    <div class="d-flex justify-space-between align-center py-2 mb-4 border-b">
      <div class="d-flex gap-2">
        <v-btn
          color="primary"
          prepend-icon="mdi-upload"
          @click="showUploadZone = true"
          class="text-none font-weight-bold"
          elevation="2"
        >
          Upload DB
        </v-btn>
      </div>

      <v-fade-transition>
        <v-chip
          v-if="status"
          :color="status.includes('Error') ? 'error' : 'success'"
          variant="tonal"
          class="font-weight-medium"
          prepend-icon="mdi-information-outline"
        >
          {{ status }}
        </v-chip>
      </v-fade-transition>
    </div>

    <!-- Loading Overlay -->
    <v-overlay
      :model-value="isDecoding"
      class="align-center justify-center"
      persistent
      scrim="white"
      style="opacity: 0.9"
    >
      <div class="d-flex flex-column align-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
          width="6"
          class="mb-4"
        ></v-progress-circular>
        <div class="text-h6 text-primary font-weight-bold">Decoding data...</div>
        <div class="text-body-2 text-medium-emphasis">Please wait while we process the database</div>
      </div>
    </v-overlay>

    <!-- Drop Zone Overlay -->
    <v-overlay
      v-model="showUploadZone"
      class="align-center justify-center"
      scrim="black"
      style="backdrop-filter: blur(4px);"
    >
      <v-card width="500" class="pa-6 text-center rounded-lg" elevation="4">
        <div
          class="drop-zone pa-10 border-dashed rounded-lg bg-grey-lighten-5 d-flex flex-column align-center justify-center cursor-pointer transition-swing"
          @drop="onDrop"
          @dragover="onDragOver"
          style="border-color: #e0e0e0;"
        >
          <v-icon icon="mdi-database-arrow-up" size="64" color="primary" class="mb-4"></v-icon>
          <h3 class="text-h6 font-weight-bold text-blue-grey-darken-3 mb-2">Drop SQLite file here</h3>
          <p class="text-body-2 text-medium-emphasis mb-6">Drag and drop your .db or .sqlite file to load data</p>

          <v-btn variant="text" color="medium-emphasis" @click="showUploadZone = false">
            Cancel
          </v-btn>
        </div>
      </v-card>
    </v-overlay>

    <!-- Autocorrect Dialog -->
    <v-dialog v-model="showAutoCorrectDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6 bg-primary text-white">
          Autocorrect Data?
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-1 mb-4">
            This machine type supports autocorrection of cement usage.
          </p>

          <div class="d-flex align-center bg-grey-lighten-5 rounded px-4 py-2 mb-4 border">
            <v-switch
              v-model="autoCorrect"
              color="primary"
              label="Enable Autocorrect"
              hide-details
              density="compact"
              class="mr-2"
            ></v-switch>
            <v-tooltip location="top" max-width="300">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" icon="mdi-information-outline" color="medium-emphasis" size="small"></v-icon>
              </template>
              <span>Corrects pump-calculated cement usage (mixture flow) using weight cell data (dry cement) to resolve discrepancies.</span>
            </v-tooltip>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="elevated" @click="processDatabase(autoCorrect)">Load Report</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Main Content -->
    <v-fade-transition mode="out-in">
      <component
        :is="currentView"
        v-if="currentView"
        :userData="rawUserData"
        :worksite="worksiteData"
        :machine="machineInfo"
        :decodedData="rawDecodedData"
      />

      <v-alert
        v-else-if="machineType"
        type="error"
        variant="tonal"
        title="Unsupported Machine Type"
        :text="`Unknown machinetype: ${machineType}`"
        class="mt-4"
      ></v-alert>

      <div v-else-if="!isDecoding && !rawDecodedData" class="d-flex flex-column align-center justify-center py-16 text-center opacity-50">
        <v-icon icon="mdi-database-off" size="80" color="grey-lighten-1" class="mb-4"></v-icon>
        <div class="text-h5 font-weight-medium text-grey-darken-1">No Data Loaded</div>
        <div class="text-body-1 text-grey-darken-1">Upload a database file to view the report</div>
      </div>
    </v-fade-transition>
  </v-container>
</template>

<style scoped>
.border-dashed {
  border-style: dashed !important;
  border-width: 2px !important;
}
.cursor-pointer {
  cursor: pointer;
}
.transition-swing {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.drop-zone:hover {
  background-color: #e3f2fd !important; /* blue-lighten-5 */
  border-color: #1976d2 !important; /* primary */
}
</style>